import {hasLimitDecimals, isInteger} from '../../utils/formUtils';
import {
    cashbackLoyaltyFormError,
    CashbackLoyaltyFormInput,
    cashbackLoyaltyFormValid,
    invalidCashbackRatioMessage,
    invalidMaxDecimalMessage
} from './FidelityProgram.form';
import {isObjectDefined} from "../../utils/object";
import {normalizeNumber, roundNumber} from "../../utils/numberUtils";

type ValidationField = {
    text: string;
    helperText: string;
};

type CashbackValidationMessage = {
    [CashbackLoyaltyFormInput.POINTS_PER_EURO]: ValidationField;
    [CashbackLoyaltyFormInput.LIMIT]: ValidationField;
    [CashbackLoyaltyFormInput.MIN_BASKET]: ValidationField;
};

type LoyaltyCashbackConfiguration = {
    cashBackPointsPerEuro: number | string | null;
    limit: number | string | null;
    minBasket: number | string | null;
};

const requiredFieldMessage = (value: string | null): string | null => {
    if (!isObjectDefined(value) || value === '') {
        return 'Ce champ est obligatoire.';
    }
    const numericValue = Number(value);
    if (isNaN(numericValue)) {
        return 'Veuillez saisir un nombre valide.';
    }
    if (numericValue <= 0) {
        return 'Ce champ doit être strictement positif.';
    }
    return null
}

const checkFormValidity = (id: string, value: string | null): string => {
    switch (id) {
        case CashbackLoyaltyFormInput.LIMIT: {
            const requiredField = requiredFieldMessage(value)
            if (requiredField) {
                return requiredField
            }
            return isInteger(value!) ? '' : 'Veuillez saisir un nombre entier.';
        }
        case CashbackLoyaltyFormInput.POINTS_PER_EURO: {
            const requiredField = requiredFieldMessage(value)
            if (requiredField) {
                return requiredField
            }
            return hasLimitDecimals(value!, 2) ? '' : invalidMaxDecimalMessage(2);
        }
        case CashbackLoyaltyFormInput.MIN_BASKET: {
            if (!value) {
                return '';
            }
            return hasLimitDecimals(value!, 2) ? '' : invalidMaxDecimalMessage(2);
        }
        default:
            return '';
    }
};

const buildFieldValidation = (id: string, value: string | null): ValidationField => {
    const helperText = checkFormValidity(id, value);

    return {
        text: helperText?.length ? cashbackLoyaltyFormError : cashbackLoyaltyFormValid,
        helperText
    };
};

const extractPointsPerEuroValidationMessage = (pointsPerEuroValidation: ValidationField, cashbackRatioErrorMessage: string): ValidationField => {
    if (pointsPerEuroValidation?.helperText) {
        return pointsPerEuroValidation;
    }
    return cashbackRatioErrorMessage ? {
        text: cashbackLoyaltyFormError,
        helperText: cashbackRatioErrorMessage
    } : {
        text: cashbackLoyaltyFormValid,
        helperText: ''
    };
};

export const getCashbackValidationMessage = (config: LoyaltyCashbackConfiguration): CashbackValidationMessage => {
    const pointsPerEuroValidation = buildFieldValidation(
        CashbackLoyaltyFormInput.POINTS_PER_EURO,
        (config?.cashBackPointsPerEuro as string)?.toString() ?? ''
    );

    const cashbackRatioErrorMessage = getCashbackRatioErrorMessage({
        cashBackPointsPerEuro: config?.cashBackPointsPerEuro ?? '',
        limit: config?.limit ?? ''
    });
    const pointsPerEuroValidationMessage = extractPointsPerEuroValidationMessage(pointsPerEuroValidation, cashbackRatioErrorMessage);
    return {
        [CashbackLoyaltyFormInput.POINTS_PER_EURO]: pointsPerEuroValidationMessage,
        [CashbackLoyaltyFormInput.LIMIT]: buildFieldValidation(
            CashbackLoyaltyFormInput.LIMIT,
            (config?.limit as string)?.toString() ?? ''
        ),

        [CashbackLoyaltyFormInput.MIN_BASKET]: buildFieldValidation(
            CashbackLoyaltyFormInput.MIN_BASKET,
            (config?.minBasket as string)?.toString() ?? ''
        )
    };
};

export const handleChange = (event, validationMessage: CashbackValidationMessage, setValidationMessage) => {
    const {id, value} = event.target;

    setValidationMessage({
        ...validationMessage,
        [id]: buildFieldValidation(id, value)
    });
};

export const getCashbackRatioErrorMessage = (config: Partial<LoyaltyCashbackConfiguration>): string => {
    const safeCashbackPointsPerEuro = normalizeNumber(config?.cashBackPointsPerEuro!);
    const safeLimit = normalizeNumber(config?.limit!);

    if (safeLimit <= 0) {
        return '';
    }

    const pointsPerEuroCashback = roundNumber(safeCashbackPointsPerEuro / safeLimit);

    return pointsPerEuroCashback <= 0 ? invalidCashbackRatioMessage : '';
};

export const cashbackPointsPerEuroForStorageIsInvalid = (config: LoyaltyCashbackConfiguration): boolean => !!getCashbackRatioErrorMessage(config);

export const cashbackConfigHasError = (
    config: LoyaltyCashbackConfiguration,
    validationMessage: CashbackValidationMessage = {} as CashbackValidationMessage
): boolean => {
    return (
        validationMessage?.[CashbackLoyaltyFormInput.POINTS_PER_EURO]?.text === cashbackLoyaltyFormError ||
        validationMessage?.[CashbackLoyaltyFormInput.LIMIT]?.text === cashbackLoyaltyFormError ||
        validationMessage?.[CashbackLoyaltyFormInput.MIN_BASKET]?.text === cashbackLoyaltyFormError ||
        !isObjectDefined(config.cashBackPointsPerEuro) ||
        config.cashBackPointsPerEuro === '' ||
        !isObjectDefined(config.limit) ||
        config.limit === '' ||
        cashbackPointsPerEuroForStorageIsInvalid(config)
    );
};